import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { useToast } from '../components/Toast.js'
import { Button } from '../components/ui/Button.js'
import { EmptyState } from '../components/ui/EmptyState.js'
import { EditorChooser } from '../components/editor/EditorChooser.js'
import { defaultDrawingName } from '../components/editor/useTrackDrawState.js'
import { friendlyError } from '../lib/friendlyError.js'
import { useLocale } from '../locale/LocaleContext.js'

// RouteEditor — thin "new route" entry. Both authoring paths create the route
// record first, then redirect into the unified detail edit view:
// - 上传 GPX: createRouteFromGpx → /routes/:id?edit=1
// - 手绘: createRoute (blank draft for the chosen activity) → /routes/:id?edit=1&draw=1
// The edit view hosts the single drawing surface (TrackDrawPanel + DrawingMap);
// there is no longer a separate full-screen DrawingEditor here.

export function RouteEditor() {
  return <EditorShell />
}

export function RouteNewPage() {
  return <RouteEditor />
}

function EditorShell() {
  const auth = useAuth()
  const navigate = useNavigate()
  const { t } = useLocale()
  const { t: drawT } = useTranslation('common')
  const { showToast } = useToast()
  const token = auth.status === 'authed' ? auth.token : null

  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const creatingRef = useRef(false)

  async function onPickGpx(picked: File | null) {
    if (!picked || !token) return
    if (creatingRef.current) return
    creatingRef.current = true
    setCreating(true)
    setError(null)
    try {
      const { route: created } = await api.createRouteFromGpx(
        picked,
        { activity: 'hiking', status: 'draft' },
        token,
      )
      showToast({ message: t('routeNewUploadSuccess'), tone: 'success' })
      navigate(`/routes/${created.id}?edit=1`)
    } catch (err) {
      setError(friendlyError(err, t))
    } finally {
      setCreating(false)
      creatingRef.current = false
    }
  }

  async function onDraw() {
    if (!token) return
    if (creatingRef.current) return
    creatingRef.current = true
    setCreating(true)
    setError(null)
    try {
      const { route: created } = await api.createRoute(
        {
          title: defaultDrawingName(drawT),
          summary: '',
          activities: ['hiking'],
          status: 'draft',
        },
        token,
      )
      navigate(`/routes/${created.id}?edit=1&draw=1`)
    } catch (err) {
      setError(friendlyError(err, t))
    } finally {
      setCreating(false)
      creatingRef.current = false
    }
  }

  if (auth.status === 'loading') {
    return <p className="p-6 text-sm text-[var(--xr-text-muted)]">…</p>
  }
  if (auth.status !== 'authed' || !token) {
    return (
      <EmptyState
        variant="signin"
        title={t('routeNewSignInTitle')}
        description={t('routeNewSignInDescription')}
        action={
          <Link to="/signin">
            <Button>{t('signIn')}</Button>
          </Link>
        }
      />
    )
  }

  return (
    <div
      className="xr-workspace-container flex min-h-[calc(100svh-7rem)] flex-col p-4 sm:p-6"
      data-testid="route-editor"
    >
      <section className="flex w-full flex-1 flex-col gap-4" data-testid="route-create-shell">
        {(creating || error) && (
          <div
            className="flex flex-col gap-1 rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-3 shadow-sm"
            data-testid="route-create-status-card"
            role={error ? 'alert' : 'status'}
          >
            <p className="text-sm font-medium text-[var(--xr-text-primary)]">
              {error ? t('editor.create.statusNeedsAttention') : t('editor.create.statusCreating')}
            </p>
            {error ? (
              <p className="text-xs text-[var(--xr-danger)]" data-testid="route-create-error">
                {error}
              </p>
            ) : (
              <p className="text-xs text-[var(--xr-text-muted)]">{t('editor.create.statusHint')}</p>
            )}
          </div>
        )}

        <div className="mx-auto w-full max-w-3xl">
          {creating ? (
            <div
              className="flex flex-col items-center gap-4 rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] py-12"
              data-testid="route-create-progress"
            >
              <p className="text-sm text-[var(--xr-text-secondary)]">{t('editor.gpxParsing')}</p>
            </div>
          ) : (
            <EditorChooser
              onPickGpx={(picked) => void onPickGpx(picked)}
              onDraw={() => void onDraw()}
            />
          )}
        </div>
      </section>
    </div>
  )
}
